import { Icon } from '@cwos/ui-admin';
import { hubForPath, type HubCard } from '../hubs.js';
import { useNav } from '../navigation.js';
import { mayOpenScreen, usePermissions } from '../permissions.js';

/**
 * The compact top strip (owner navigation directive §2/§4):
 *
 *   SIDEBAR    = PRODUCT MAP        (AppShell's accordion)
 *   TOP STRIP  = QUICK ORIENTATION  ← this
 *   MAIN CANVAS= ACTUAL WORK
 *
 * Skinny, side-by-side rounded rectangles — icon + destination name in one
 * ~34px line, `[ Schedule ] [ Time & Timesheets ] [ Tasks ]` — visually
 * secondary to the work surface, which begins immediately underneath. The
 * one-line orientation text a card used to carry lives on as its tooltip.
 *
 * DELIBERATELY LESS THAN THE SIDEBAR (directive §5): the strip renders only
 * the hub's MAJOR destinations (`hub.cards`); secondary ones (`hub.more`,
 * e.g. Location history) stay in the sidebar accordion, contextual links and
 * global Find, so the shortcut strip never becomes another overloaded
 * navigation system.
 *
 * Permission-shaped like the rail (absent, never grayed out), by the same
 * `mayOpenScreen` rule, so the strip can never offer what the rail would
 * not. Nothing renders until the permission set has RESOLVED, and the strip
 * hides when it could only repeat the page it is on or on paths outside any
 * hub.
 *
 * LIVE CONTEXT ("Decisions · 6") is optional and arrives with each hub's own
 * phase, where the surface already holds the read — the strip firing count
 * queries per navigation would spend more than it says, and a count derived
 * from a refused read would zero-lie (four-state doctrine). The label span
 * is the seam it lands beside.
 */
export function HubCards({ path }: { path: string }) {
  const { permissions, isLoading, isError } = usePermissions();
  const nav = useNav();
  const hub = hubForPath(path);
  if (!hub || isLoading || isError) return null;

  const visible = hub.cards.filter((c) => mayOpenScreen(permissions, c.permission));
  const current = currentCard(visible, path);
  if (visible.length === 0 || visible.every((c) => c === current)) return null;

  return (
    <nav className="hub-strip" aria-label={`${hub.label} destinations`}>
      {visible.map((card) => (
        <a
          key={card.key}
          className="hub-strip-item"
          href={card.href}
          title={card.descriptor}
          data-current={card === current}
          aria-current={card === current ? 'page' : undefined}
          onClick={(e) => {
            e.preventDefault();
            nav(card.href);
          }}
        >
          <Icon name={card.icon} size={14} />
          <span className="hub-strip-label">{card.label}</span>
        </a>
      ))}
    </nav>
  );
}

/**
 * The card this path is inside — longest matching href wins, so on
 * `/time/approvals` the Decisions card is current rather than Time &
 * Timesheets (`/time`), which merely prefixes it. `/` matches only itself:
 * every path starts with it, and a Right Now card lit on every screen of the
 * Today hub would be a wrong sentence.
 */
function currentCard(cards: readonly HubCard[], path: string): HubCard | undefined {
  let best: HubCard | undefined;
  for (const card of cards) {
    const owns = path === card.href || (card.href !== '/' && path.startsWith(`${card.href}/`));
    if (!owns) continue;
    if (!best || card.href.length > best.href.length) best = card;
  }
  return best;
}
